import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import { htmlSafe } from '@ember/template';
import { type SafeString } from '@ember/template/-private/handlebars';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import PowerSelect from 'ember-power-select/components/power-select';
import CalendarGrid from 'garaje/components/calendar-grid';
import type DateLimits from 'garaje/types/date-time/date-limits';
import { type CalendarDate } from 'garaje/utils/date-fns-tz-utilities';
import { TIMES } from 'garaje/utils/format-minutes-as-time';
import moment from 'moment-timezone';

type TimeFormat = `${number}:${number} ${'am' | 'pm'}`;

interface EnvoyTwoUpCalendarComponentSignature {
  Args: {
    classNames?: string;
    dateLimits?: DateLimits;
    disableTransition?: boolean;
    endDate?: CalendarDate;
    endTime?: CalendarDate;
    events?: moment.Moment[];
    noShadow?: boolean;
    startDate?: CalendarDate;
    startTime?: CalendarDate;
    inline?: boolean;
    showTimePicker?: boolean;
    dateRangeSelected?: (startDate: CalendarDate, endDate: CalendarDate) => void;
    didSelectDateRange?: (startDate: CalendarDate, endDate: CalendarDate) => void;
    handleGoBackOneMonth?: (date: Date) => void;
    handleGoToNextMonth?: (date: Date) => void;
    setEndDate?: (date: CalendarDate) => void;
    setStartDate?: (date: CalendarDate) => void;
    setEndTime?: (date: CalendarDate) => void;
    setStartTime?: (date: CalendarDate) => void;
  };
  Element: HTMLDivElement;
}

export default class EnvoyTwoUpCalendarComponent extends Component<EnvoyTwoUpCalendarComponentSignature> {
  @tracked isSelectingStartDate = true;
  @tracked date;
  @tracked offset = 0;

  times = TIMES;

  get shadowClass(): string {
    return this.args.noShadow ? 'no-shadow' : '';
  }

  get popoverStyles(): SafeString {
    return htmlSafe(`left: ${this.offset}px;`);
  }

  get selectedStartDate(): moment.Moment {
    return moment(this.args.startDate);
  }

  get selectedEndDate(): moment.Moment {
    return moment(this.args.endDate);
  }

  get selectedStartTime(): string {
    return moment(this.args.startTime).format('h:mm a');
  }

  get selectedEndTime(): string {
    return moment(this.args.endTime).format('h:mm a');
  }

  get currentMonthFormatted(): string {
    return this.currentMonth.format('MMMM');
  }

  get currentMonth(): moment.Moment {
    return moment(this.date).startOf('month');
  }

  get nextMonthFormatted(): string {
    return this.nextMonth.format('MMMM');
  }

  get nextMonth(): moment.Moment {
    return moment(this.date).add(1, 'month');
  }

  constructor(...args: ConstructorParameters<typeof Component<EnvoyTwoUpCalendarComponentSignature>>) {
    super(...args);
    this.date = moment(this.args.startDate).startOf('month');
  }

  @action
  goBackOneMonth(): void {
    this.date = moment(this.date).subtract(1, 'month');
    this.args.handleGoBackOneMonth?.(this.date.toDate());
  }

  @action
  goToNextMonth(): void {
    this.date = moment(this.date).startOf('month').add(1, 'month');
    this.args.handleGoToNextMonth?.(this.date.toDate());
  }

  @action
  selectDate(selectedDate: moment.Moment): void {
    if (this.isSelectingStartDate) {
      const startMonth = moment(selectedDate).startOf('month');

      this.date = startMonth;
      this.isSelectingStartDate = false;
      let startOfDay;
      if (this.selectedStartTime) {
        startOfDay = moment(selectedDate).startOf('day');
      } else {
        startOfDay = moment(selectedDate).startOf('day');
      }
      this.args.setStartDate?.(startOfDay);

      if (this.selectedStartDate.isAfter(this.selectedEndDate)) {
        const endOfDay = moment(selectedDate).endOf('day');
        this.args.setEndDate?.(endOfDay);
      }
      if (this.args.startDate && this.args.endDate) {
        this.args.didSelectDateRange?.(this.args.startDate, this.args.endDate);
      }
    } else {
      const startMonth = moment(this.args.startDate).startOf('month');
      const selectedStartDate = this.selectedStartDate;

      this.args.setEndDate?.(selectedDate.endOf('day'));
      this.date = startMonth;
      this.isSelectingStartDate = true;

      if (this.selectedEndDate.isBefore(selectedStartDate)) {
        this.args.setStartDate?.(selectedDate.startOf('day'));
        this.args.setEndDate?.(selectedStartDate.endOf('day'));
      }

      if (this.args.startDate && this.args.endDate) {
        this.args.didSelectDateRange?.(this.args.startDate, this.args.endDate);
        this.args.dateRangeSelected?.(this.args.startDate, this.args.endDate);
      }
    }
  }

  @action
  selectMonth(selectedMonth: CalendarDate): void {
    this.args.setStartDate?.(moment(selectedMonth).startOf('month'));
    this.args.setEndDate?.(moment(selectedMonth).endOf('month'));

    if (this.args.startDate && this.args.endDate) {
      this.args.didSelectDateRange?.(this.args.startDate, this.args.endDate);
      this.args.dateRangeSelected?.(this.args.startDate, this.args.endDate);
    }
  }

  @action
  checkPositioning(): void {
    // fix positioning if popover is offscreen
    const $popover = document.querySelector('.calendar-popover');
    if (!$popover) return;

    const clientRect = $popover.getBoundingClientRect();
    const xRightPosition = clientRect.x + clientRect.width;

    if (xRightPosition > window.innerWidth) {
      this.offset = window.innerWidth - xRightPosition - 20;
    }
  }

  @action
  setStartTime(time: TimeFormat): void {
    const { hour, minute } = this.parseTimeString(time);
    const startDateWithTime = this.selectedStartDate.hour(hour).minute(minute);

    this.args.setStartTime?.(startDateWithTime);
    this.args.setStartDate?.(startDateWithTime);
  }

  @action
  setEndTime(time: TimeFormat): void {
    const { hour, minute } = this.parseTimeString(time);
    const endDateWithTime = this.selectedStartDate.hour(hour).minute(minute);

    this.args.setEndTime?.(endDateWithTime);
    this.args.setEndDate?.(endDateWithTime);
  }

  parseTimeString(timeString: TimeFormat): { hour: number; minute: number } {
    const [time = '', amPm = 'am'] = timeString.split(' ');
    const [hour = '0', minute = '0'] = time.split(':');
    let formattedHour = Number.parseInt(hour, 10);

    formattedHour = formattedHour % 12;

    if (amPm === 'pm') formattedHour = formattedHour + 12;

    return { hour: formattedHour, minute: Number.parseInt(minute, 10) };
  }

  <template>
    <div class="dashboardCalendar" {{didInsert this.checkPositioning}} ...attributes>
      <div
        class="calendar-popover twoup bg-white
          {{if @inline 'inline'}}
          {{this.shadowClass}}
          {{if @disableTransition '!transition-none'}}"
        style={{this.popoverStyles}}
      >
        <div class="float-left box-border w-6/12 bg-white" style="padding:0 10px;" data-test-left-calendar>
          <div class="dashboardCalendar-header flex">
            <div
              role="button"
              class="previous btn-reset cursor-pointer"
              {{on "click" this.goBackOneMonth}}
              data-test-go-back-one-month
            ></div>
            <span
              class="m-auto cursor-pointer"
              role="button"
              data-test-current-month
              {{on "click" (fn this.selectMonth this.currentMonth)}}
            >
              {{this.currentMonthFormatted}}
            </span>
          </div>
          <CalendarGrid
            @currentMonth={{this.currentMonth}}
            @dateLimits={{@dateLimits}}
            @selectedStartDate={{this.selectedStartDate}}
            @selectedEndDate={{this.selectedEndDate}}
            @hideTrailingDayCells={{true}}
            @selectDate={{this.selectDate}}
            @events={{@events}}
          />
          {{#if @showTimePicker}}
            <div class="flex flex-row items-center">
              <PowerSelect
                @searchEnabled={{false}}
                @selected={{this.selectedStartTime}}
                @options={{this.times}}
                @onChange={{this.setStartTime}}
                @dropdownClass="powerSelectDropdown--access-event-log-time-picker"
                class="w-25 mr-2 mt-4"
                as |time|
              >
                {{time}}
              </PowerSelect>
              to
              <PowerSelect
                @searchEnabled={{false}}
                @selected={{this.selectedEndTime}}
                @options={{this.times}}
                @onChange={{this.setEndTime}}
                @dropdownClass="powerSelectDropdown--access-event-log-time-picker"
                class="w-25 ml-2 mt-4"
                as |time|
              >
                {{time}}
              </PowerSelect>
            </div>
          {{/if}}
        </div>
        <div class="float-left box-border w-6/12 bg-white" style="padding:0 10px;" data-test-right-calendar>
          <div class="dashboardCalendar-header flex">
            <span
              class="m-auto cursor-pointer"
              role="button"
              {{on "click" (fn this.selectMonth this.nextMonth)}}
              data-test-next-month
            >
              {{this.nextMonthFormatted}}
            </span>
            <div
              role="button"
              class="next btn-reset cursor-pointer"
              {{on "click" this.goToNextMonth}}
              data-test-go-to-next-month
            ></div>
          </div>
          <CalendarGrid
            @currentMonth={{this.nextMonth}}
            @dateLimits={{@dateLimits}}
            @selectedStartDate={{this.selectedStartDate}}
            @selectedEndDate={{this.selectedEndDate}}
            @hideLeadingDayCells={{true}}
            @selectDate={{this.selectDate}}
            @events={{@events}}
          />
        </div>
      </div>
    </div>
  </template>
}
